import { css, html, LitElement } from 'lit'
import { property } from 'lit/decorators.js'
import type { StatFormat, StatSize } from '@/components/Stat/types'
import { safeCustomElement } from '@/utilities/custom-element'

function formatCompact(value: string | number): string {
  const n = typeof value === 'string' ? Number(value) : value
  if (Number.isNaN(n)) return String(value)
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return n.toString()
}

@safeCustomElement('mdc-stat')
export class MdcStat extends LitElement {
  @property({ type: String }) icon = ''
  @property({ type: String }) value: string | number = ''
  @property({ type: String }) label = ''
  @property({ type: String }) size: StatSize = 'md'
  @property({ type: String }) format: StatFormat = 'default'

  static styles = css`
		:host {
			display: block;
			font-family: var(--typography-font-family-sans);
			--_mdc-stat-padding: var(--spacing-025, 0.25rem);
			--_mdc-stat-gap: var(--spacing-025, 0.25rem);
			--_mdc-stat-value-size: var(--text-087-and-half, 0.875rem);
			--_mdc-stat-label-size: var(--text-075, 0.75rem);
			--_mdc-stat-icon-size: 0.875rem;
			--_mdc-stat-value-row-gap: var(--spacing-012-and-half, 0.125rem);
			--_mdc-stat-align-items: center;
			--_mdc-stat-text-align: center;
			--_mdc-stat-value-color: var(--color-functional-gray-black);
			--_mdc-stat-label-color: var(--color-functional-gray-400);
		}

		:host([size='sm']) {
			--_mdc-stat-padding: var(--spacing-012-and-half, 0.125rem);
			--_mdc-stat-gap: var(--spacing-012-and-half, 0.125rem);
			--_mdc-stat-value-size: var(--text-075, 0.75rem);
			--_mdc-stat-label-size: var(--text-062-and-half, 0.625rem);
			--_mdc-stat-icon-size: 0.75rem;
			--_mdc-stat-value-row-gap: var(--spacing-012-and-half, 0.125rem);
		}

		:host([size='lg']) {
			--_mdc-stat-padding: var(--spacing-025, 0.25rem);
			--_mdc-stat-gap: var(--spacing-025, 0.25rem);
			--_mdc-stat-value-size: var(--text-125, 1.25rem);
			--_mdc-stat-label-size: var(--text-075, 0.75rem);
			--_mdc-stat-icon-size: 1.25rem;
			--_mdc-stat-value-row-gap: var(--spacing-025, 0.25rem);
		}

		:host([size='xl']) {
			--_mdc-stat-padding: var(--spacing-050, 0.5rem);
			--_mdc-stat-gap: var(--spacing-050, 0.5rem);
			--_mdc-stat-value-size: var(--text-175, 1.75rem);
			--_mdc-stat-label-size: var(--text-087-and-half, 0.875rem);
			--_mdc-stat-icon-size: 1.5rem;
			--_mdc-stat-value-row-gap: var(--spacing-050, 0.5rem);
		}

		:host([size='2xl']) {
			--_mdc-stat-padding: var(--spacing-075, 0.75rem);
			--_mdc-stat-gap: var(--spacing-050, 0.5rem);
			--_mdc-stat-value-size: var(--text-250, 2.5rem);
			--_mdc-stat-label-size: var(--text-100, 1rem);
			--_mdc-stat-icon-size: 2rem;
			--_mdc-stat-value-row-gap: var(--spacing-050, 0.5rem);
		}

		:host([size='3xl']) {
			--_mdc-stat-padding: var(--spacing-100, 1rem);
			--_mdc-stat-gap: var(--spacing-075, 0.75rem);
			--_mdc-stat-value-size: var(--text-300, 3rem);
			--_mdc-stat-label-size: var(--text-125, 1.25rem);
			--_mdc-stat-icon-size: 2.5rem;
			--_mdc-stat-value-row-gap: var(--spacing-075, 0.75rem);
		}

		.mdc-stat {
			display: inline-flex;
			flex-direction: column;
			align-items: var(--_mdc-stat-align-items);
			text-align: var(--_mdc-stat-text-align);
			box-sizing: border-box;
			padding: var(--_mdc-stat-padding);
			gap: var(--_mdc-stat-gap);
		}

		.mdc-stat__value-row {
			display: inline-flex;
			align-items: center;
			gap: var(--_mdc-stat-value-row-gap);
		}

		.mdc-stat__value-row mdc-icon {
			flex: 0 0 auto;
			width: var(--_mdc-stat-icon-size);
			height: var(--_mdc-stat-icon-size);
		}

		.mdc-stat__value {
			color: var(--_mdc-stat-value-color);
			font-weight: 450;
			line-height: 1.3;
			font-size: var(--_mdc-stat-value-size);
		}

		:host([size='lg']) .mdc-stat__value,
		:host([size='xl']) .mdc-stat__value,
		:host([size='2xl']) .mdc-stat__value,
		:host([size='3xl']) .mdc-stat__value {
			font-weight: 600;
			letter-spacing: -0.00625rem;
		}

		.mdc-stat__label {
			color: var(--_mdc-stat-label-color);
			font-weight: 450;
			line-height: 1.2;
			font-size: var(--_mdc-stat-label-size);
		}
	`

  render() {
    return html`
			<div class="mdc-stat">
				<div class="mdc-stat__value-row">
					${this.icon ? html`<mdc-icon name=${this.icon}></mdc-icon>` : ''}
					<span class="mdc-stat__value">${this.format === 'compact' ? formatCompact(this.value) : this.value}</span>
				</div>
				<span class="mdc-stat__label">${this.label}</span>
			</div>
		`
  }
}
